// 첫 진입 스플래시(로딩) 화면(개선항목 18).
// 앱을 처음 열 때(세션 시작) 딱 한 번만 표시한다. 관리자가 운영설정에 등록한 스플래시 이미지를 보여주고,
// 미설정이면 기본 브랜드 화면(마스코트 로고 + 상호)을 표시한다.
//  - "세션당 1회" 판정에 sessionStorage 플래그(oo-splash-shown)를 쓴다. sessionStorage 는 같은 세션의
//    전체 페이지 리로드(결제창/SNS 로그인 리다이렉트 복귀 등)에는 유지되고, 앱을 완전히 닫았다 다시 열 때만
//    초기화된다. 따라서 리다이렉트 복귀 등 "앱으로 돌아오는" 모든 상황에선 스플래시가 다시 뜨지 않는다.
//  - 다음 세션에서 깜빡임 없이 즉시 표시되도록 직전 이미지 URL 을 localStorage 에 캐시한다.
//  - 최소 표시 시간 후 페이드 아웃하며 사라진다. SPA 내 화면 전환에서는 App 이 재마운트되지 않아 다시 뜨지 않는다.

import { useEffect, useState } from "react";
import { useStoreSettings } from "@/features/operations/useStoreSettings.ts";
import { imageUrl } from "@/features/catalog/imageUrl.ts";

const SPLASH_URL_CACHE = "oo-splash-url";
// 세션당 1회 노출 판정 플래그(sessionStorage). 세션 시작(앱 최초 진입) 때만 없다.
const SPLASH_SHOWN_FLAG = "oo-splash-shown";
// 첫 진입 스플래시는 최소 2초간 노출한다(개선항목 10). 이후 페이드 아웃.
const MIN_DURATION_MS = 2000;
const FADE_MS = 300;

// 첫 진입 스플래시 오버레이. 최소 표시 시간 후 페이드 아웃하며 언마운트된다.
export default function SplashScreen() {
  const ops = useStoreSettings();
  // 세션당 1회만 노출한다. 이미 이 세션에서 표시했으면(리로드/리다이렉트 복귀 등) 건너뛴다.
  const [visible, setVisible] = useState(() => {
    if (typeof sessionStorage === "undefined") {
      return true;
    }
    if (sessionStorage.getItem(SPLASH_SHOWN_FLAG)) {
      return false;
    }
    // 이번 세션의 첫 진입 - 표시하고 플래그를 남겨 이후(복귀/리로드)엔 다시 뜨지 않게 한다.
    sessionStorage.setItem(SPLASH_SHOWN_FLAG, "1");
    return true;
  });
  const [fading, setFading] = useState(false);
  // 직전 콜드스타트에 캐시한 URL 을 초기값으로 써서 첫 프레임부터 이미지를 보여준다.
  const [imgUrl, setImgUrl] = useState<string | null>(() => localStorage.getItem(SPLASH_URL_CACHE));

  // 운영설정 로드 시 스플래시 이미지 URL 갱신 + 캐시 동기화.
  useEffect(() => {
    const settings = ops.settings;
    if (!settings) {
      return;
    }
    const url = settings.splashImageKey ? imageUrl(settings.splashImageKey) : null;
    setImgUrl(url);
    if (url) {
      localStorage.setItem(SPLASH_URL_CACHE, url);
    } else {
      localStorage.removeItem(SPLASH_URL_CACHE);
    }
  }, [ops.settings]);

  // 최소 표시 시간 후 페이드 아웃 시작.
  useEffect(() => {
    const t = setTimeout(() => setFading(true), MIN_DURATION_MS);
    return () => clearTimeout(t);
  }, []);

  // 페이드 종료 후 DOM 에서 제거.
  useEffect(() => {
    if (!fading) {
      return;
    }
    const t = setTimeout(() => setVisible(false), FADE_MS);
    return () => clearTimeout(t);
  }, [fading]);

  if (!visible) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-page transition-opacity duration-300 ${
        fading ? "opacity-0" : "opacity-100"
      }`}
    >
      {imgUrl ? (
        <img src={imgUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex flex-col items-center gap-3">
          <img src="/icons/icon-512.png" alt="" className="h-20 w-20 rounded-2xl" />
          <span className="text-2xl font-bold text-brand-700">열린축산</span>
        </div>
      )}
    </div>
  );
}
